import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useDidMountEffect from 'assets/js/useDidMountEffect';
import { confirmAlert } from 'react-confirm-alert';

/* MODAL */
import { useModal } from 'hooks/modalContext';

/* JS */
import * as Server from 'assets/js/Server';
import * as Common from 'assets/js/Common';

/* DatePicker */
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

const CheckOut = () => {
  const { openModal } = useModal();

  const today = moment();
  const monday = today.startOf('isoWeek');
  const mondayDate = monday.format('YYYY-MM-DD');

  const [commuteMstList, setCommuteMstList] = useState([{
    commuteSeq: 0,
    fkWorkerId: '',
    workerName: '',
    fkCustMstSeq: 0,
    custName: '',
    workDate: '',
    outTime: '',
    getOffState: '',
    getOffStateName: '',
    getOffLat: '',
    getOffLon: '',
    stepsPerDay: 0,
    leaveReason: '',
    leaveCheckYn: '',
    leaveCheckYnName: '',
    lat: '',
    lon: '',
  }]);
  const [custMstList, setCustMstList] = useState([{
    custMstSeq: 0,
    custName: '',
  }]);
  const [search, setSearch] = useState({
    fkCustMstSeq: 0,
    startDate: mondayDate,
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [isSearch, setIsSearch] = useState(false);

  useEffect(() => {
    Common.isLogin();

    getCustMstList();
  }, []);

  useDidMountEffect(() => {
    if (isSearch) {
      getCommuteMstList();
    }
  }, [isSearch]);

  const getCustMstList = () => {
    Server.sendGet('client/select/all', {}, getCustMstListCallback).then();
  };

  const getCustMstListCallback = res => {
    setSearch(() => {
      return {
        ...search,
        fkCustMstSeq: res?.['resultList']?.[0]?.custMstSeq,
      }
    });
    setCustMstList(res?.['resultList']);
    setIsSearch(true);
  };

  const getCommuteMstList = () => {
    Server.sendGet('workforce/commute', {
      custMstSeq: search?.fkCustMstSeq,
      startDate: search?.startDate,
      endDate: search?.endDate,
    }, getCommuteMstListCallback).then();
  };

  const getCommuteMstListCallback = res => {
    res?.['resultList'].forEach(v => {
      if (v.getOffStateName === 'Absence' || v.getOffStateName === 'Early leave') {
        Object.assign(v, { className: 'problem', background: '#ffb1b1' });
      } else if (v.getOffStateName === 'Before') {
        Object.assign(v, { className: 'before', background: '#b2c7f9' });
      } else {
        Object.assign(v, { className: 'onTime', background: '#b3ffab' });
      }
    });

    setCommuteMstList(res?.['resultList']);
  };

  const handleContent = (item, isModal) => {
    const today = moment().startOf('day');
    const workDate = moment(item.workDate);

    if (today.isBefore(workDate)) {
      return;
    }

    const getModalData = (modalName) => ({
      modalName,
      modalData: {
        isRegi: false,
        data: {
          item,
          callback: getCommuteMstList,
        },
      },
    });

    if (isModal === 'reason') {
      if (item.getOffState === 'N' || item.getOffState === 'A') {
        openModal(getModalData('WorkforceCheckOutModal'));
      } else if (!Common.isEmpty(item.attendanceState) && Common.isEmpty(item.getOffState)) {
        openModal(getModalData('WorkforceGetOffWorkModal'));
      }
    } else if (isModal === 'nfc' && item.commuteSeq !== 0) {
      openModal(getModalData('NfcHistoryModal'));
    } else if (isModal === 'cancel' && item.commuteSeq !== 0) {
      let currentState = '1';

      if (item.attendanceState === 'N' && item.lateCheckYn === 'N') {
        currentState = '2';
      } else if (item.attendanceState === 'N' && item.lateCheckYn === 'Y') {
        currentState = '3';
      }

      confirmAlert({
        title: 'Are you sure you want to return?',
        buttons: [{
          label: 'Yes',
          onClick: () => {
            Server.sendPut('workforce/commute/cancel', {
              commuteSeq: item.commuteSeq,
              fkWorkerId: item.fkWorkerId,
              currentState: currentState,
            }, changeStatusCallback).then();
          }
        }, {
          label: 'No'
        }]
      });
    }
  };

  const changeStatusCallback = () => {
    Common.errorMsg('Cancel Complete.');

    getCommuteMstList();
  };

  const handleStartDateChange = date => handleDateChange(date, true);
  const handleEndDateChange = date => handleDateChange(date, false);

  const formatDate = date => moment(date['$d']).format('YYYY-MM-DD');

  const handleDateChange = (date, isStart) => {
    const formattedDate = formatDate(date);

    setSearch(search => {
      if (isStart) {
        const isBeforeDate = moment(search.endDate).isBefore(formattedDate);

        return {
          ...search,
          startDate: formattedDate,
          endDate: isBeforeDate ? formattedDate : search.endDate,
        };
      } else {
        const isBeforeDate = moment(formattedDate).isBefore(search.startDate);

        return {
          ...search,
          startDate: isBeforeDate ? formattedDate : search.startDate,
          endDate: formattedDate,
        };
      }
    });
  };

  const openMap = item => {
    const { commuteSeq, getOffLat, getOffLon } = item;

    if (Common.isEmpty(commuteSeq)) {
      return;
    }

    if (Common.isEmpty(getOffLat) || Common.isEmpty(getOffLon)) {
      Common.errorMsg('The location is not verified.');

      return;
    }

    const url = `https://www.google.com/maps/search/?api=1&query=${getOffLat},${getOffLon}`;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Inner>
        <Title>
          <TitHead>
            <i/>
            <h4>Check-out management</h4>
          </TitHead>
        </Title>
        <SearchArea>
          <CateIn>
            <Cate>
              <CateSel
                name='workForm'
                id='workForm'
                value={search?.fkCustMstSeq}
                onChange={e => {
                  setSearch(() => {
                    return {
                      ...search,
                      fkCustMstSeq: e.target.value,
                    };
                  });
                }}
              >
                {custMstList?.map(item => (
                  <option key={item.custMstSeq} value={item.custMstSeq}>{item.custName}</option>
                ))}
              </CateSel>
            </Cate>
          </CateIn>
          <DateIn>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker', 'DatePicker']}>
                <DatePicker
                  onChangeRaw={e => e.preventDefault()}
                  format={'YYYY-MM-DD'}
                  value={dayjs(search?.startDate) || ''}
                  onChange={date => handleStartDateChange(date)}
                />
              </DemoContainer>
            </LocalizationProvider>
            <i/>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker', 'DatePicker']}>
                <DatePicker
                  onChangeRaw={e => e.preventDefault()}
                  format={'YYYY-MM-DD'}
                  value={dayjs(search?.endDate) || ''}
                  onChange={date => handleEndDateChange(date)}
                />
              </DemoContainer>
            </LocalizationProvider>
          </DateIn>
          <SearchBtn onClick={getCommuteMstList}>Search</SearchBtn>
        </SearchArea>
        <Content>
          <Table>
            <colgroup>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
              <col width='8.5%'/>
            </colgroup>
            <thead>
            <tr>
              <th>No</th>
              <th>ID</th>
              <th>Name</th>
              <th>Client</th>
              <th>Location</th>
              <th>Work Date</th>
              <th>Number of walks</th>
              <th>Get off work time</th>
              <th>Status</th>
              <th>Team Leader confirmation</th>
              <th>Check-out</th>
              <th>NFC</th>
            </tr>
            </thead>
            <tbody>
            {commuteMstList?.map((item, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{item.fkWorkerId}</td>
                <td>{item.workerName}</td>
                <td>{item.custName}</td>
                <td>
                  <TitBtn onClick={() => openMap(item)}>
                    <span>Show</span>
                  </TitBtn>
                </td>
                <td>{item.workDate}</td>
                <td>{item.stepsPerDay}</td>
                <td>{item.outTime}</td>
                <td
                  style={{ background: item.background }}
                  className={item.className}
                  onClick={() => {
                    handleContent(item, 'reason');
                  }}
                >
                  {moment(moment().format('YYYY-MM-DD')).isBefore(item.workDate) ? '' : item.getOffStateName}
                </td>
                <td>{item.leaveCheckYnName}</td>
                <td>
                  {!Common.isEmpty(item.getOffState) &&
                    <TitBtn onClick={() => handleContent(item, 'cancel')}>
                      <span>Cancel</span>
                    </TitBtn>
                  }
                </td>
                <td>
                  <TitBtn onClick={() => handleContent(item, 'nfc')}>
                    <span>NFC</span>
                  </TitBtn>
                </td>
              </tr>
            ))}
            {Common.isEmpty(commuteMstList) &&
              <tr>
                <td colSpan={11} className={'none-data'}>
                  There is no data to output.
                </td>
              </tr>
            }
            </tbody>
          </Table>
        </Content>
      </Inner>
    </>
  );
}

const Inner = styled.div`
  flex: 1;
  background: #ffffff;
`;

const Title = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
`;

const TitHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0 10px;

  & i {
    display: block;
    width: 4px;
    height: 32px;
    border-radius: 0 2px 2px 0;
    background: #1e1e1e;
  }

  & h4 {
    font-weight: 500;
    font-size: 16px;
    color: #000000;
  }
`;

const SearchArea = styled.div`
  position: relative;
  margin-top: 35px;
  padding: 15px 20px;
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  gap: 0 15px;
  width: 100%;

  & .react-datepicker__tab-loop {
    position: absolute;
  }
`;

const DateIn = styled.div`
  display: flex;
  align-items: center;
  gap: 0 15px;

  .MuiStack-root {
    padding: 0;

    .MuiInputBase-root {
      background: #f8f8f8;
    }
  }

  & input {
    width: 116px;
    height: 40px;
    line-height: 40px;
    padding: 0 16px;
    font-weight: 400;
    font-size: 16px;
    color: #777777;
    border-radius: 5px;
    outline: none;
  }

  & i {
    display: block;
    width: 12px;
    height: 1px;
    background: rgba(0, 0, 0, 0.12);
  }

  & .react-datepicker-popper {
    width: 226px;
    z-index: 99;
  }
`;

const CateIn = styled.div`
  display: flex;
  align-items: center;
  gap: 0 20px;
`;

const Cate = styled.div`
  display: flex;
  align-items: center;
  gap: 0 10px;

  & label {
    font-weight: 400;
    font-size: 16px;
    color: #777777;
  }
`;

const CateSel = styled.select`
  width: 200px;
  height: 40px;
  line-height: 40px;
  padding: 0 16px;
  font-weight: 400;
  font-size: 16px;
  color: #777777;
  background: #f8f8f8 url('https://ust-india.s3.ap-northeast-2.amazonaws.com/erp-web/sel_arrow.svg') top 50% right 10px no-repeat;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 5px;
  outline: none;
`;

const SearchBtn = styled.button`
  width: 120px;
  height: 40px;
  line-height: 40px;
  border-radius: 5px;
  background: #a4a4a4;
  font-weight: 400;
  font-size: 16px;
  color: #ffffff;

  &:hover {
    background-color: #7e7e7e;
  }
`;

const Content = styled.div`
  margin-top: 35px;
  position: relative;
  height: 622px;
  overflow-y: scroll;
`;

const Table = styled.table`
  position: relative;

  & th {
    padding: 6px 0;
    font-weight: 400;
    font-size: 18px;
    color: #777777;
    background: #f8f8f8;
    text-align: center;
    border-bottom: 1px solid #eeeeee;
    vertical-align: middle;
  }

  & td {
    padding: 10px 0;
    font-weight: 400;
    font-size: 16px;
    color: #000000;
    text-align: center;
    vertical-align: middle;
    border-bottom: 1px solid #eeeeee;
    border-left: 1px solid #eeeeee;
    height: 47px;
  }

  & thead {
    position: sticky;
    top: 0;
  }

  & tbody > tr {
    cursor: pointer;

    &:hover {
      & td {
        background: #f4f4f4;
      }
    }
  }

  & tr td.none-data {
    height: 572px;
    border: 0;
  }

  & tr td:first-child {
    border-left: none;
  }

  & tr:nth-child(even) {
    background: #fcfcfc;
  }
`;

const TitBtn = styled.button`
  height: 40px;
  line-height: 40px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  gap: 0 5px;
  background: ${props => (props['$Color'] ? '#0072b9' : '#a4a4a4')};
  border-radius: 5px;
  margin: auto;

  & span {
    font-weight: 500;
    font-size: 14px;
    color: #ffffff;
  }

  &:hover {
    background-color: ${props => (props['$Color'] ? '#075C97' : '#7e7e7e')};
  }
`;

export default CheckOut;